import React, { useEffect } from 'react';
import { Typography, Box, Grid, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { authApi } from '../../api/auth';
import { useUserStore } from '../../stores/userStore';
import queryClient from '../../queryClient';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BasicButton from '../../components/button/BasicButton';
import CustomTextField from '../../components/common/CustomTextField';
import theme from '../../theme';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import CustomSelectBox from '../../components/common/CustomSelectBox';
import { safeRedirectPath } from '../../utils/safeRedirectPath';

const NICKNAME_MAX_LENGTH = 14;
const NICKNAME_VALIDATION_MESSAGE = `닉네임은 한글, 영문, 숫자 (2~${NICKNAME_MAX_LENGTH}자)로 입력해야 합니다.`;
const applySocialSignupInfo = (setValue, userInfo) => {
  setValue('email', userInfo.email || '');
  setValue('nickname', userInfo.nickname || '');
  if (userInfo.birthDate) {
    const [year, month, day] = userInfo.birthDate.split('-');
    setValue('birthYear', year || '');
    setValue('birthMonth', String(Number(month)) || '');
    setValue('birthDay', String(Number(day)) || '');
  }
};

const schema = yup.object().shape({
  nickname: yup
    .string()
    .required('닉네임을 입력해주세요.')
    .min(2, NICKNAME_VALIDATION_MESSAGE)
    .max(NICKNAME_MAX_LENGTH, NICKNAME_VALIDATION_MESSAGE)
    .matches(/^[가-힣a-zA-Z0-9]+$/, NICKNAME_VALIDATION_MESSAGE),
  gender: yup.string().default(''),
  birthYear: yup.string().required('년도를 선택해주세요.').default(''),
  birthMonth: yup.string().required('월을 선택해주세요.').default(''),
  birthDay: yup.string().required('일을 선택해주세요.').default(''),
  ageAgreement: yup.boolean().oneOf([true], '만 14세 이상 및 개인정보 수집 이용에 동의해 주세요.'),
});

const SignupDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fetchUser = useUserStore((state) => state.fetchUser);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nickname: '',
      gender: '',
      birthYear: '',
      birthMonth: '',
      birthDay: '',
      ageAgreement: false,
      termsAgreement: false,
    },
  });
  const email = watch('email');
  const password = watch('password');
  const gender = watch('gender');
  const birthYear = watch('birthYear');
  const birthMonth = watch('birthMonth');
  const birthDay = watch('birthDay');

  // 소셜 로그인에서 온 경우인지 확인
  const isSocialSignup =
    !!sessionStorage.getItem('social_signup_token') || !!searchParams.get('signupToken');

  // 년도, 월, 일 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  useEffect(() => {
    const signupToken = searchParams.get('signupToken');

    if (signupToken) {
      sessionStorage.setItem('social_signup_token', signupToken);
      authApi
        .getSocialSignupInfo(signupToken)
        .then(({ data: userInfo }) => {
          applySocialSignupInfo(setValue, userInfo);
        })
        .catch((error) => {
          console.error('Failed to load social signup:', error);
          sessionStorage.removeItem('social_signup_token');
          navigate('/login', { replace: true });
        });
    } else if (sessionStorage.getItem('social_signup_token')) {
      const storedToken = sessionStorage.getItem('social_signup_token');
      authApi
        .getSocialSignupInfo(storedToken)
        .then(({ data: userInfo }) => {
          applySocialSignupInfo(setValue, userInfo);
        })
        .catch(() => {
          sessionStorage.removeItem('social_signup_token');
          navigate('/login', { replace: true });
        });
    } else {
      const storedEmail = sessionStorage.getItem('signup_email');
      const storedPassword = sessionStorage.getItem('signup_password');

      if (!storedEmail || !storedPassword) {
        navigate('/signup', { replace: true });
        return;
      }

      setValue('email', storedEmail);
      setValue('password', storedPassword);
    }
  }, [navigate, setValue, searchParams]);

  const onSubmit = async (formData) => {
    try {
      // 생년월일을 YYYY-MM-DD 형식으로 조합
      const birthDate = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;

      // 소셜 로그인에서 온 경우와 일반 회원가입을 구분
      const socialSignupToken = sessionStorage.getItem('social_signup_token');

      let userData;
      if (socialSignupToken) {
        if (!formData.termsAgreement) {
          setError('termsAgreement', {
            type: 'required',
            message: '이용약관 및 개인정보처리방침에 동의해 주세요.',
          });
          return;
        }
        userData = {
          email: email,
          password: '',
          nickname: formData.nickname,
          gender: formData.gender || null,
          birthDate: birthDate,
          socialSignupToken,
          acceptedTerms: true,
          acceptedPrivacy: true,
          confirmedMinimumAge: true,
        };
      } else {
        // 일반 회원가입
        userData = {
          email: email,
          password: password,
          nickname: formData.nickname,
          gender: formData.gender || null,
          birthDate: birthDate,
          acceptedTerms: true,
          acceptedPrivacy: true,
          confirmedMinimumAge: true,
        };
      }

      const { data } = await authApi.signup(userData);
      if (data && data.token) {
        localStorage.setItem('POSTPOP_AUTH_TOKEN', data.token);
      }

      queryClient.clear();
      await fetchUser();

      // sessionStorage 정리
      sessionStorage.removeItem('signup_email');
      sessionStorage.removeItem('signup_password');
      sessionStorage.removeItem('social_signup_token');

      const redirectUrl = safeRedirectPath(searchParams.get('redirectUrl'));
      navigate(redirectUrl, { replace: true });
    } catch (err) {
      console.error(err.message || '회원가입에 실패했습니다.');
    }
  };

  return (
    <PageLayout>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: { lg: '32.5%', md: '49%', xs: '100%' },
            minWidth: { xs: '320px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack sx={{ width: '100%' }} spacing={'30px'}>
            <Typography fontSize={{ lg: 24, md: 22, xs: 20 }} fontWeight="bold" textAlign="center">
              회원가입
            </Typography>

            <Stack>
              <input type="hidden" {...register('email')} />
              {!isSocialSignup && <input type="hidden" {...register('password')} />}
              <Stack spacing={1}>
                <Typography variant="body3">이메일</Typography>
                <CustomTextField fullWidth value={email} disabled={true} />
              </Stack>
              <Stack spacing={1} mt={'20px'}>
                <Typography variant="body3">
                  닉네임<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('nickname')}
                  error={!!errors.nickname}
                  helperText={errors.nickname?.message}
                  placeholder={`한글, 영문, 숫자 (2~${NICKNAME_MAX_LENGTH}자)`}
                />
              </Stack>
              <Stack spacing={1} mt={'20px'}>
                <Typography variant="body3">성별</Typography>
                <Stack direction="row" spacing={1}>
                  {[
                    ['male', '남성'],
                    ['female', '여성'],
                  ].map(([value, label]) => (
                    <BasicButton
                      key={value}
                      type="button"
                      variant={gender === value ? 'contained' : 'outlined'}
                      aria-pressed={gender === value}
                      onClick={() =>
                        setValue('gender', gender === value ? '' : value, { shouldDirty: true })
                      }
                      sx={{ flex: 1 }}
                    >
                      {label}
                    </BasicButton>
                  ))}
                </Stack>
              </Stack>
              <Stack spacing={1} mt={'20px'}>
                <Typography variant="body3">
                  생년월일<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <Grid container sx={{ width: '100%', gap: '9px' }}>
                  <Grid item xs>
                    <CustomSelectBox
                      options={[
                        { label: '년도', value: '' },
                        ...years.map((year) => ({
                          label: year.toString(),
                          value: year.toString(),
                        })),
                      ]}
                      value={birthYear}
                      {...register('birthYear')}
                      error={!!errors.birthYear}
                      helperText={errors.birthYear?.message}
                    />
                  </Grid>
                  <Grid item xs>
                    <CustomSelectBox
                      options={[
                        { label: '월', value: '' },
                        ...months.map((month) => ({
                          label: month.toString(),
                          value: month.toString(),
                        })),
                      ]}
                      value={birthMonth}
                      {...register('birthMonth')}
                      error={!!errors.birthMonth}
                      helperText={errors.birthMonth?.message}
                    />
                  </Grid>
                  <Grid item xs>
                    <CustomSelectBox
                      options={[
                        { label: '일', value: '' },
                        ...days.map((day) => ({ label: day.toString(), value: day.toString() })),
                      ]}
                      value={birthDay}
                      {...register('birthDay')}
                      error={!!errors.birthDay}
                      helperText={errors.birthDay?.message}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Stack>

          {/* 14세 이상 개인정보 동의 */}
          {isSocialSignup && (
            <Box display="flex" mt={'30px'} sx={{ width: '100%', flexDirection: 'column', gap: 1 }}>
              <CustomCheckbox
                {...register('termsAgreement')}
                error={!!errors.termsAgreement}
                helperText={errors.termsAgreement?.message}
                label={'이용약관 및 개인정보처리방침에 동의합니다.'}
              />
              <Typography variant="caption" sx={{ pl: 4 }}>
                <a href="/about/terms-of-use" target="_blank" rel="noreferrer">
                  이용약관
                </a>
                {' · '}
                <a href="/about/privacy-policy" target="_blank" rel="noreferrer">
                  개인정보처리방침
                </a>
              </Typography>
            </Box>
          )}
          <Box display="flex" mt={'30px'} mb={'30px'} sx={{ width: '100%' }}>
            <CustomCheckbox
              {...register('ageAgreement')}
              error={!!errors.ageAgreement}
              helperText={errors.ageAgreement?.message}
              label={'본인은 만 14세 이상이며 개인정보 수집 및 이용에 동의합니다.'}
              secondLabel={'(만 14세 미만의 경우, 법정대리인의 동의가 필요합니다.)'}
            />
          </Box>

          <BasicButton
            type="submit"
            fullWidth
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            회원가입 완료
          </BasicButton>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default SignupDetail;
